import React, { Dispatch, SetStateAction, useState } from 'react';
import { View, StyleSheet, Text } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import Colors from '../../../../../constants/Colors';
import Style from '../../../../../constants/Style';
import deleteArea from '../../../../../requests/Locations/Areas/DeleteArea';
import EditAreaModal from './EditAreaModal';
import { updateBuildingId } from '../../../../../redux/selectedBuildingSlice';
import { AppScreen } from '../../../../../navigation/LinkingConfiguration';
import { UserPermission } from '../../../../../constants/Types';
import {
  Area, Floor, LocationSubtypeTitles, Space,
} from '../../../../../components/Map/SetFloorsModal/FloorItem';
import PressableStyled from '../../../../../components/PressableStyled';
import AreaIcon from '../../../../../components/24pxIcons/AreaIcon';
import PriorityIndicator from '../../PriorityIndicator';
import LocationIcon from '../../../../../components/30pxIcons/LocationIcon';
import EditIcon24 from '../../../../../components/24pxIcons/EditIcon24';
import DeleteIcon24 from '../../../../../components/24pxIcons/DeleteIcon24';

interface AreaItemProps {
  parentName: string,
  area: Area,
  setSelectedAreaId: Dispatch<SetStateAction<string>>,
  removeArea: Function,
  updateArea,
  showScreenAlert: Function,
  setLoading: Dispatch<SetStateAction<boolean>>,
  selectedFloor: Floor,
  testID?: string,
}

const AreaItem = ({
  area, parentName, updateArea, removeArea, setSelectedAreaId, showScreenAlert, setLoading,
  selectedFloor, testID,
}: AreaItemProps) => {
  if (!area) return null;

  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const happyEnabled: boolean = useSelector((state: { happyEnabled: { enabled: boolean } }) => state
    .happyEnabled.enabled);
  const canUpdateLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_UPDATE);
  const canDeleteLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_DELETE);
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const [editAreaModalShown, setEditModalShown] = useState<boolean>(false);

  const deleteAreaError: Function = (errorText?: string) => showScreenAlert(
    'Error',
    errorText
    ?? 'Could not delete area at the moment. Please try again later.',
  );

  const remove: () => void = () => showScreenAlert(
    'Delete area?',
    <Text>
      {'Do you want to delete '}
      <Text style={Style.alertBoldText}>{area.Name}</Text>
      {' from '}
      <Text style={Style.alertBoldText}>{parentName}</Text>
      {'?'}
    </Text>,
    true,
    () => {
      setLoading(true);
      deleteArea(area.LocationId).then((res) => {
        if (res && !res.isError) {
          removeArea(area.LocationId);
          showScreenAlert(
            `Deleted ${area.Name}`,
            <Text>
              <Text style={Style.alertBoldText}>{area.Name}</Text>
              {' was successfully deleted from '}
              <Text style={Style.alertBoldText}>{parentName}</Text>
            </Text>,
            false,
          );
        } else deleteAreaError(res?.errorText);
        setLoading(false);
      }).catch((error) => {
        setLoading(false);
        deleteAreaError(error?.message);
      });
    },
    'Delete',
  );

  const openInMap = () => {
    dispatch(updateBuildingId(selectedFloor?.ParentId));
    const floorId: string = selectedFloor?.LocationId;
    const areaId: string = area?.LocationId;
    navigation.navigate(AppScreen.MAP as any, { floorId, areaId });
  };

  const selectArea: () => void = () => setSelectedAreaId(area?.LocationId);

  const openEditAreaModal: () => void = () => setEditModalShown(true);

  const hideEditAreaModal: () => void = () => setEditModalShown(false);

  let assetCount: number = area.assetCount ?? 0;
  let sensorCount: number = area.sensorCount ?? 0;
  let meterCount: number = area.meterCount ?? 0;
  let spaceCount: number = 0;
  Object.values(area.spaces ?? {}).forEach((space: Space) => {
    if (!space) return;
    assetCount += space.assetCount ?? 0;
    sensorCount += space.sensorCount ?? 0;
    meterCount += space.meterCount ?? 0;
    spaceCount += 1;
  });

  return <>
    <PressableStyled
      style={[Style.row, styles.container]}
      onPress={selectArea}
      {...{ testID }}
    >
      <View style={[Style.row, styles.titleSection]}>
        <View style={styles.iconContainer}>
          <AreaIcon />
        </View>
        <View>
          <Text style={styles.title}>{area.Name}</Text>
          {LocationSubtypeTitles[area.LocationSubtype]?.length > 0
            && <Text style={styles.label}>{LocationSubtypeTitles[area.LocationSubtype]}</Text>}
        </View>
      </View>
      <View style={[Style.row, styles.prioritySection]}>
        <PriorityIndicator isHighPriority={area.IsHighPriority} />
      </View>
      <View style={styles.planSection}>
        <Text style={styles.title}>{spaceCount}</Text>
      </View>
      <View style={styles.planSection}>
        <Text style={styles.title}>{assetCount}</Text>
      </View>
      {happyEnabled && <View style={styles.planSection}>
        <Text style={styles.title}>{sensorCount + meterCount}</Text>
      </View>}
      <View style={[Style.row, styles.actionSection]}>
        <PressableStyled
          onPress={openInMap}
          testID='area-navigate-to-map-btn'
        >
          <LocationIcon color={Colors.light.hereworksBlue500} />
        </PressableStyled>
        {canUpdateLocations && <PressableStyled
          onPress={openEditAreaModal}
          testID='edit-area-btn'
        >
          <EditIcon24 style={styles.editIcon} />
        </PressableStyled>}
        {canDeleteLocations && <PressableStyled
          onPress={remove}
          testID='delete-area-btn'
        >
          <DeleteIcon24 />
        </PressableStyled>}
      </View>
    </PressableStyled>
    <EditAreaModal
      {...{
        area,
        updateArea,
        showScreenAlert,
        removeArea,
        parentName,
        editAreaModalShown,
        hideEditAreaModal,
      }}
    />
  </>;
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.light.hereworksWhite100,
    borderRadius: 10,
    marginTop: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  titleSection: {
    flex: 0.368,
    justifyContent: 'flex-start',
  },
  prioritySection: {
    flex: 0.13,
  },
  planSection: { flex: 0.1 },
  actionSection: {
    flex: 0.21,
    textAlign: 'right',
    justifyContent: 'flex-end',
  },
  iconContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    width: 40,
    aspectRatio: 1,
    borderRadius: 20,
    backgroundColor: Colors.light.hereworksDarkBlue,
    marginEnd: 12,
  },
  title: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
  },
  number: {
    fontSize: 12,
    lineHeight: 15,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  editIcon: {
    marginHorizontal: 12,
  },
  label: {
    fontSize: 12,
    lineHeight: 14,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
});

export default AreaItem;
